import { Button } from "devextreme-react/button";
import { TextBox } from "devextreme-react/text-box";
import notify from "../../../lib/notify";
import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RentalHistoriesByEmployee2 } from "../../../api/sharingEmployees2";
import {
  formatDateOrIndefinite,
  HistoryEmployee2,
  HistoryEntry2,
} from "../types";

type Props = {
  employees: HistoryEmployee2[];
  isMobile: boolean;
  loading: boolean;
};

const actionColorMap: Record<string, string> = {
  CREATE_SHARE: "transfer2-history-tag-create",
  EDIT_RELEASE_RANGE: "transfer2-history-tag-edit",
  ACCEPT_RENTAL: "transfer2-history-tag-accept",
  WITHDRAW_RENTAL: "transfer2-history-tag-withdraw",
  WITHDRAW_RENTAL_ADMIN: "transfer2-history-tag-withdraw",
  WITHDRAW_RENTAL_EMPLOYMENT_ENDED: "transfer2-history-tag-withdraw",
  REMOVE_SHARE: "transfer2-history-tag-remove",
};

export const HistoryView2 = memo(function HistoryView2({
  employees,
  isMobile,
  loading,
}: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<HistoryEmployee2 | null>(null);
  const [entries, setEntries] = useState<HistoryEntry2[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      [e.lastName, e.firstName, e.position, e.companyName]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q)),
    );
  }, [employees, search]);

  useEffect(() => {
    if (!selected) {
      setEntries([]);
      return;
    }
    let cancel = false;
    setEntriesLoading(true);
    RentalHistoriesByEmployee2(selected.companyId, selected.employeeId)
      .then((res) => {
        if (cancel) return;
        if (res.isOk) {
          setEntries(res.data);
        } else {
          setEntries([]);
          notify(`${t("Error:")} ${res.error}`, "error");
        }
      })
      .finally(() => {
        if (!cancel) setEntriesLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [selected, t]);

  const actionLabel = (type: string) => {
    const key = `transferOfEmployees2.actionType.${type}`;
    const translated = t(key);
    return translated === key ? type : translated;
  };

  const employeeList = (
    <div className="transfer2-history-list">
      <div className="transfer2-history-search">
        <TextBox
          value={search}
          placeholder={t("transferOfEmployees.Search...")}
          showClearButton
          mode="search"
          valueChangeEvent="keyup input"
          onValueChanged={(e) => setSearch(e.value || "")}
        />
      </div>
      {loading ? (
        <div className="transfer2-empty">{t("loading")}...</div>
      ) : filtered.length === 0 ? (
        <div className="transfer2-empty">
          {t("transferOfEmployees2.emptyHistory")}
        </div>
      ) : (
        filtered.map((emp) => {
          const active =
            selected?.companyId === emp.companyId &&
            selected?.employeeId === emp.employeeId;
          return (
            <button
              key={`${emp.companyId}-${emp.employeeId}`}
              className={`transfer2-history-item ${active ? "is-active" : ""}`}
              onClick={() => setSelected(emp)}
              type="button"
            >
              <div className="transfer2-history-item-name">
                {emp.lastName} {emp.firstName}
              </div>
              <div className="transfer2-history-item-meta">
                {emp.companyName}
                {emp.position ? ` · ${emp.position}` : ""}
              </div>
              <div className="transfer2-history-item-count">
                {t("transferOfEmployees2.historyEntries", { count: emp.count })}
              </div>
            </button>
          );
        })
      )}
    </div>
  );

  const detail = (
    <div className="transfer2-history-detail">
      {!selected ? (
        <div className="transfer2-empty">
          {t("transferOfEmployees2.pickEmployee")}
        </div>
      ) : entriesLoading ? (
        <div className="transfer2-empty">{t("loading")}...</div>
      ) : entries.length === 0 ? (
        <div className="transfer2-empty">
          {t("transferOfEmployees2.noHistoryEntries")}
        </div>
      ) : (
        <>
          <div className="transfer2-history-detail-header">
            <Button
              icon="back"
              stylingMode="text"
              visible={isMobile}
              onClick={() => setSelected(null)}
            />
            <div>
              <div className="transfer2-history-detail-name">
                {selected.lastName} {selected.firstName}
              </div>
              <div className="transfer2-history-detail-meta">
                {selected.companyName}
                {selected.position ? ` · ${selected.position}` : ""}
              </div>
            </div>
          </div>
          <ul className="transfer2-history-entries">
            {entries.map((entry) => (
              <li key={entry.id} className="transfer2-history-entry">
                <div
                  className={`transfer2-history-tag ${
                    actionColorMap[entry.actionType] || ""
                  }`}
                >
                  {actionLabel(entry.actionType)}
                </div>
                <div className="transfer2-history-entry-date">
                  {formatDateOrIndefinite(
                    entry.actionDate,
                    t("transferOfEmployees2.indefinite"),
                  )}
                </div>
                <div className="transfer2-history-entry-body">
                  <div>
                    <span className="transfer2-history-entry-label">
                      {t("transferOfEmployees2.owner")}:
                    </span>{" "}
                    {entry.managerFullName || "—"}
                  </div>
                  {entry.temporaryManagerFullName && (
                    <div>
                      <span className="transfer2-history-entry-label">
                        {t("transferOfEmployees2.borrower")}:
                      </span>{" "}
                      {entry.temporaryManagerFullName}
                    </div>
                  )}
                  <div>
                    <span className="transfer2-history-entry-label">
                      {t("transferOfEmployees2.rangeStart")}:
                    </span>{" "}
                    {formatDateOrIndefinite(
                      entry.startDateOfTheRental,
                      t("transferOfEmployees2.indefinite"),
                    )}
                  </div>
                  <div>
                    <span className="transfer2-history-entry-label">
                      {t("transferOfEmployees2.rangeEnd")}:
                    </span>{" "}
                    {formatDateOrIndefinite(
                      entry.endDateOfTheRental,
                      t("transferOfEmployees2.indefinite"),
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div className="transfer2-history-mobile">
        {selected ? detail : employeeList}
      </div>
    );
  }

  return (
    <div className="transfer2-history-layout">
      {employeeList}
      {detail}
    </div>
  );
});
